import React, { useContext } from "react";
import { sortBy } from "lodash";
import { useHistory, useLocation, useParams } from "react-router";
import { OBJECT_TYPE, WORKFLOW_STATUS_MODE } from "common/constants";
import { buildLink, INFO_PANEL_TAB_NAME } from "common/routing";
import { getObjectDisplay } from "common-client/utils/customObject";
import { GetWorkflowQuery, useGetWorkflowQuery } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { ButtonLink } from "../Common/ButtonLink";
import { Button } from "../Common/Buttons/Button";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import { ProgressBar } from "../Common/ProgressBar";
import { StyleVariant, Tag } from "../Common/Tag";
import { Body, Title } from "../Common/Typography";
import { ApplicationInfo, WorkflowContainer } from "./__styles__/Workflow";
import { WorkflowStep } from "./WorkflowStep";
import { getWorkflowProgress, getWorkflowStatus } from "./workflowUtils";

export const Workflow = () => {
  const { state } = useLocation<{ prevLocation?: string }>();
  const prevLocation = state?.prevLocation ?? buildLink("map");
  const { workflowId } = useParams<{
    workflowId: string;
  }>();

  const { data, loading } = useGetWorkflowQuery({
    variables: { workflowId },
    fetchPolicy: "network-only",
  });

  if (loading || !data?.workflow) {
    return null;
  }

  const workflowName = data.workflow.workflowDefinition.name;

  return (
    <FullPageFormLayout
      subtitle={workflowName}
      prevLocation={prevLocation}
      width="wide"
    >
      <WorkflowContent workflow={data.workflow} />
    </FullPageFormLayout>
  );
};

export const WorkflowContent = ({
  workflow,
}: {
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const history = useHistory();
  const { isPublic } = useContext(AuthContext);

  const objectDisplay =
    workflow.property ?
      getObjectDisplay({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
      })
    : null;

  const orderedWorkflowSteps = sortBy(workflow.workflowSteps, "order");

  const isManualMode =
    workflow.workflowDefinition.statusMode === WORKFLOW_STATUS_MODE.MANUAL;

  const { completed: completedCount, total: totalSteps } =
    getWorkflowProgress(workflow);

  const propertyUrl =
    isPublic ?
      buildLink("residentProperty", {
        propertyId: workflow.property!.id,
      })
    : buildLink(
        "map",
        {},
        {
          propertyId: workflow.property!.id,
          lat: workflow.property!.latitude.toString(),
          lng: workflow.property!.longitude.toString(),
          address: workflow.property!.fullAddress,
          tab: INFO_PANEL_TAB_NAME.WORKFLOWS,
        },
      );

  return (
    <WorkflowContainer isPublic={isPublic} data-testid="workflow-container">
      {isPublic && (
        <ButtonLink
          styleVariant="primary"
          size="medium"
          leftIconName="chevron-left"
          onClick={() =>
            history.push(
              buildLink("listWorkflows", {
                workflowDefinitionId: workflow.workflowDefinition.id,
              }),
            )
          }
          style={{ width: "fit-content" }}
        >
          {workflow.workflowDefinition.name}
        </ButtonLink>
      )}
      <ApplicationInfo>
        <FlexColumn gap="s">
          <FlexRow
            gap="s"
            style={{
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Body size="small" type="emphasis" color="contentSecondary">
              APPLICATION
            </Body>
            <Tag styleVariant={workflow.status.display as StyleVariant}>
              {getWorkflowStatus(
                workflow.status.text,
                workflow.workflowDefinition.statusMode,
              )}
            </Tag>
          </FlexRow>
        </FlexColumn>

        <FlexColumn gap="s">
          {objectDisplay && (
            <Title size="default" type="semiBold">
              {objectDisplay}
            </Title>
          )}
        </FlexColumn>
        {workflow.property && (
          <FlexRow gap="s" style={{ alignItems: "center" }}>
            <Button
              variant="secondary"
              size="small"
              onPress={() => history.push(propertyUrl)}
              title="View property"
            />
          </FlexRow>
        )}
      </ApplicationInfo>
      <ProgressBar value={completedCount} max={totalSteps} />
      <FlexColumn gap="m">
        {orderedWorkflowSteps.map(step => (
          <WorkflowStep
            key={step.id}
            workflowStep={step}
            isManualMode={isManualMode}
            objectId={workflow.property?.id}
            objectType={
              workflow.property?.id ? OBJECT_TYPE.PROPERTY : undefined
            }
            workflow={workflow}
          />
        ))}
      </FlexColumn>
    </WorkflowContainer>
  );
};
